import React, { useState, useEffect } from "react";
import { Typography, IconButton } from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useNavigate } from "react-router";
import { makeStyles } from "@material-ui/core/styles";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import ApiService from "./../../service/ApiService";
import Pathnames from "../../model/enum/Pathnames";
import "./reviews.css";
import Misc from "../../utils/Misc";

const useStyles = makeStyles((theme) => ({
  title: {
    color: "white",
    fontSize: "2rem",
    fontWeight: "bold",
    letterSpacing: "1px",
    marginTop: "2rem",
    marginBottom: "2rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.3rem",
      marginTop: "1rem",
      marginBottom: "1rem",
    },
  },
}));

interface Review {
  author_name: string;
  author_url: string;
  language: string;
  profile_photo_url: string;
  rating: number;
  relative_time_description: string;
  text: string;
  time: number;
}

const NR_ITEMS = 3;

const GoogleReviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const classes = useStyles();

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await ApiService.get<any>(DatabaseEntities.PLACES);

        if (response.status == "OK" && response?.result) {
          setReviews(response.result.reviews || []);
        } else {
          setError("No reviews found");
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (Misc.isArrayEmpty(reviews)) fetchReviews();
  });

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex - NR_ITEMS < 0
        ? Math.max(0, reviews.length - NR_ITEMS)
        : prevIndex - NR_ITEMS
    );
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex + NR_ITEMS >= reviews.length ? 0 : prevIndex + NR_ITEMS
    );
  };

  const visibleReviews = reviews.slice(currentIndex, currentIndex + NR_ITEMS);

  if (loading) return <div>Loading reviews...</div>;

  return (
    <div className="google-reviews">
      <Typography
        variant="h4"
        component="h2"
        gutterBottom
        className={classes.title}
      >
        What our customers say
      </Typography>

      <div className="reviews-carousel">
        <IconButton
          className="carousel-button prev"
          onClick={prevSlide}
          disabled={reviews.length <= NR_ITEMS}
        >
          <ChevronLeft fontSize="medium" style={{ color: "white" }} />
        </IconButton>

        <div className="reviews-container">
          {visibleReviews.map((review, index) => (
            <div key={`${review.time}-${index}`} className="review-card">
              <div className="review-header">
                <img
                  src={
                    review.profile_photo_url || "https://via.placeholder.com/50"
                  }
                  alt={review.author_name}
                  className="review-avatar"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://via.placeholder.com/50";
                  }}
                />
                <div>
                  <h6>{review.author_name}</h6>
                  <small className="review-time">
                    {new Date(review.time * 1000).toLocaleDateString()}
                  </small>
                </div>
              </div>
              <hr style={{ color: "lightgrey" }} />
              <div className="d-flex row w-100 justify-content-start">
                <div className="review-rating">
                  {Array(5)
                    .fill(0)
                    .map((_, i) => (
                      <span
                        key={i}
                        className={`star ${i < review.rating ? "filled" : ""}`}
                      >
                        ★
                      </span>
                    ))}
                </div>
              </div>
              <p className="review-text">{review.text}</p>
            </div>
          ))}
        </div>

        <IconButton
          className="carousel-button next"
          onClick={nextSlide}
          disabled={reviews.length <= NR_ITEMS}
        >
          <ChevronRight fontSize="medium" style={{ color: "white" }} />
        </IconButton>
      </div>

      <div className="carousel-dots">
        {Array(Math.ceil(reviews.length / NR_ITEMS))
          .fill(0)
          .map((_, i) => (
            <span
              key={i}
              className={`dot ${currentIndex / NR_ITEMS === i ? "active" : ""}`}
              onClick={() => setCurrentIndex(i * NR_ITEMS)}
            />
          ))}
      </div>

      <div className="button-container">
        <button className="button" onClick={() => navigate(Pathnames.REVIEWS)}>
          Reviews
        </button>
      </div>
    </div>
  );
};

export default GoogleReviews;
